/***
 * MODULES.WIDGET
 * List of modules avaiables for an account
 */

import { connect } from "react-redux"
import { WithTranslation, withTranslation } from "react-i18next"
import { Session } from "@/redux/_session.types"
import { AccountFeatures, AccountModules, AccountState, createEmptyAccountRisksSettings } from "@/redux/account.types"
import { forOwn } from "lodash"
import {
  faBullseye,
  faChartLine,
  faChartSimple,
  faClipboardCheck,
  faGlobe,
  faGraduationCap,
  faLanguage,
  faListCheck,
  faMessage,
  faQrcode,
  faRankingStar,
  faScaleBalanced,
  faSitemap,
  faSquarePollVertical,
  faUsers,
  faWandMagicSparkles,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons"
import ListItem from "@/components/list-item"
import Checkbox from "@/components/checkbox"
import Space from "@/components/space"
import Chip from "@/components/chip"
import ToggleButton from "@/components/toggle-button"
import ListCell from "@/components/list-cell"
import ListFields from "@/components/list-fields"
import { accountEdit } from "@/redux/account.actions"
import { store } from "@/core/root-reducer"

//Modules that send data to an AI service : grouped under one chip that switches them all at once
const AI_MODULES: { [feature: string]: string[] } = { survey: ["assistant", "summarize", "translate", "projectAssistant"] }

interface StateProps extends WithTranslation {
  _session: Session
  account: AccountState
}

interface OwnProps {
  readonly?: boolean
  onSave?: Function
  modules: AccountModules
  features: AccountFeatures
}

type Props = StateProps & OwnProps

//Icon displayed before each module
const MODULE_ICONS: { [key: string]: IconDefinition } = {
  qrcode: faQrcode,
  sms: faMessage,
  segment: faSitemap,
  languages: faLanguage,
  learning: faGraduationCap,
  nps: faBullseye,
  rps: faChartLine,
  task: faListCheck,
  assistant: faWandMagicSparkles,
  populations: faUsers,
  summarize: faSquarePollVertical,
  benchmark: faRankingStar,
  projectAssistant: faClipboardCheck,
  translate: faGlobe,
  scales: faScaleBalanced,
  inequalities: faChartSimple
}

//Modules only for the team of IDTree : the client never sees them in the list of his modules
const INTERNAL_MODULES: string[] = ["inequalities"]

function ModulesWidget(props: Props) {
  const { t } = props
  // Get list of modules to display
  function getReadyOnly() {
    const features: { feature: string; modules: string[] }[] = []

    forOwn(props.features, (value, key) => {
      const modulesRaw = props.modules[key]

      if (modulesRaw) {
        const activeModules = Object.entries(modulesRaw)
          .filter(([, v]) => v === true)
          .filter(([k]) => !INTERNAL_MODULES.includes(k))
          .map(([k]) => k)

        if (value) {
          features.push({
            feature: t("feature_" + key),
            modules: activeModules
          })
        }
      } else if (value) {
        // If feature is true but no modules
        features.push({
          feature: t("feature_" + key),
          modules: []
        })
      }
    })

    if (features.length) {
      return (
        <div>
          {features.map((f, index) => (
            <div key={index} style={{ marginBottom: "4px" }}>
              <div>{f.feature}</div>
              {f.modules.length > 0 && (
                <div className="grey-t" style={{ fontSize: "12px" }}>
                  {f.modules.map((m) => t("module_" + m)).join(", ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )
    } else {
      return <span className="grey-t">{t("utils_empty")}</span>
    }
  }

  function selectModule(feature: string, module: string, value: boolean) {
    store.dispatch(
      accountEdit("modules", {
        ...props.modules,
        [feature]: {
          ...(props.modules[feature] || {}),
          [module]: value
        }
      })
    )

    save()
  }

  //Switch every AI module of a feature : off if they are all on, otherwise on
  function selectAiModules(feature: string) {
    const value = !AI_MODULES[feature].every((module) => props.modules[feature][module])
    store.dispatch(
      accountEdit("modules", {
        ...props.modules,
        [feature]: {
          ...(props.modules[feature] || {}),
          ...Object.fromEntries(AI_MODULES[feature].map((module) => [module, value]))
        }
      })
    )

    save()
  }

  async function selectFeature(feature: string, value: boolean) {
    store.dispatch(accountEdit("features", Object.assign({}, props.features, { [feature]: value })))

    // Initialize account risk settings when the feature is enabled.
    if (feature === "risks" && value) {
      store.dispatch(accountEdit("accountRisksSettings", createEmptyAccountRisksSettings()))
      save()
    } else {
      save()
    }
  }

  function selectAllowMultipleEstablishments(value: boolean) {
    store.dispatch(accountEdit("accountRisksSettings", {
      ...createEmptyAccountRisksSettings(),
      ...props.account.active.accountRisksSettings,
      allowMultipleEstablishments: value
    }))
    save()
  }

  function save() {
    setTimeout(() => {
      if (props.onSave) {
        props.onSave()
      }
    }, 200)
  }

  return props.readonly ? (
    <div>{getReadyOnly()}</div>
  ) : (
    <div>
      <ListFields>
        <ListCell width={200}>Fonctionnalités</ListCell>
        <ListCell width={200}>{t("account_modules")}</ListCell>
      </ListFields>

      {Object.keys(new AccountFeatures()).map((feature) => (
        <ListItem key={feature} isSmall>
          <ListCell width={200}>
            <Checkbox
              active={props.features[feature]}
              text={t("feature_" + feature)}
              onClick={() => selectFeature(feature, !props.features[feature])}
            />
            <Space />
          </ListCell>
          {(props.modules[feature] || feature === "risks") && (
            <div>
              {props.features[feature] && (
                <div className="flex1 flex flex-wrap">
                  {Object.keys(new AccountModules()[feature] ?? {})
                    .filter((module) => !AI_MODULES[feature]?.includes(module))
                    .map((module) => (
                      <Chip
                        onClick={() => selectModule(feature, module, !props.modules[feature][module])}
                        isWithMargin
                        icon={MODULE_ICONS[module]}
                        key={module}
                        color={props.modules[feature][module] ? props._session.accountColors.active : null}>
                        {t("module_" + module)}
                      </Chip>
                    ))}

                  {AI_MODULES[feature] && (
                    <div
                      className="flex flex-wrap items-center rounded-full border border-dashed border-idt-grey-main pr-1"
                      style={{ margin: "-1px 4px" }}>
                      <div className="flex items-center pl-2 pr-1">
                        <ToggleButton
                          isActive={AI_MODULES[feature].every((module) => props.modules[feature][module])}
                          onToogle={() => selectAiModules(feature)}
                        />
                        <b className="ml-2">{t("module_ai_group")}</b>
                      </div>
                      {AI_MODULES[feature].map((module) => (
                        <Chip
                          onClick={() => selectModule(feature, module, !props.modules[feature][module])}
                          isWithMargin
                          icon={MODULE_ICONS[module]}
                          key={module}
                          color={props.modules[feature][module] ? props._session.accountColors.active : null}>
                          {t("module_" + module)}
                        </Chip>
                      ))}
                    </div>
                  )}

                  {feature === "risks" && (
                    <Chip
                      onClick={() =>
                        selectAllowMultipleEstablishments(
                          !(props.account.active.accountRisksSettings?.allowMultipleEstablishments ?? false)
                        )
                      }
                      isWithMargin
                      color={
                        props.account.active.accountRisksSettings?.allowMultipleEstablishments
                          ? props._session.accountColors.active
                          : null
                      }>
                      {t("feature_allowMultipleEstablishments")}
                    </Chip>
                  )}
                </div>
              )}
            </div>
          )}
        </ListItem>
      ))}
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  account: state.account
})

export default connect(mapStateToProps)(withTranslation()(ModulesWidget))
